import { useEffect } from 'react';

import {
  checkoutService,
  pathQueryParams,
  SIGN_UP_PAYMENT_RESULT_TYPE,
  signUpSubscriptionsService,
  useSelectSubscription,
  UseSelectSubscriptionsReturn,
} from '../../../payment';
import { getCurrentUrl, goTo } from '../../../shared/utils/angularUtils';
import { ConsultationType } from '../../../shared/constants/coach/consultationType';

type UseSignUpSubscriptionsReturn = UseSelectSubscriptionsReturn & {
  handleSubmit: () => void;
};

type UseSignUpSubscriptionsParams = {
  entityId: number;
  customerEmail: string;
  consultationType: ConsultationType;
  challengeId?: number;
};

export const useSignUpSubscriptions = ({
  entityId,
  customerEmail,
  consultationType,
  challengeId,
}: UseSignUpSubscriptionsParams): UseSignUpSubscriptionsReturn => {
  const onApplyPromoCode = async (promoCode: string) => {
    return signUpSubscriptionsService.applyPromoCode(customerEmail, entityId, promoCode);
  };

  const selectSubscriptionInfo = useSelectSubscription({ onApplyPromoCode });
  const { selectedSubscription, isNonPaidSubscription, setSubscriptions } =
    selectSubscriptionInfo;

  const getSubscriptions = async () => {
    const response = await signUpSubscriptionsService.getSubscriptions(
      entityId,
      customerEmail,
    );

    setSubscriptions(response);
  };

  useEffect(() => {
    getSubscriptions();
  }, []);

  const handleSubmit = async () => {
    if (!selectedSubscription || !entityId || !customerEmail) {
      throw new Error('Not all required data is set');
    }

    const currentUrl = getCurrentUrl();

    if (isNonPaidSubscription) {
      const result = await checkoutService.createNonPaidSubscription(
        entityId,
        customerEmail,
        selectedSubscription.id,
      );

      goTo('/payment-result', {
        params: {
          [pathQueryParams.redirectStatus]: 'succeeded',
          [pathQueryParams.subscriptionType]: selectedSubscription.subscriptionType,
          [pathQueryParams.paymentTimeEpoch]: result.paymentTimeEpoch,
          [pathQueryParams.senderName]: result.senderName,
          [pathQueryParams.paymentResultType]: SIGN_UP_PAYMENT_RESULT_TYPE.REGISTRATION,
          [pathQueryParams.consultationType]: consultationType,
          [pathQueryParams.challengeId]: challengeId,
        },
      });
    } else {
      goTo('/checkout', {
        params: {
          [pathQueryParams.entityId]: entityId,
          [pathQueryParams.email]: customerEmail,
          [pathQueryParams.selectedSubscriptionId]: selectedSubscription.id,
          [pathQueryParams.retryPath]: currentUrl,
          [pathQueryParams.paymentResultType]: SIGN_UP_PAYMENT_RESULT_TYPE.REGISTRATION,
          [pathQueryParams.subscriptionType]: selectedSubscription.subscriptionType,
          [pathQueryParams.consultationType]: consultationType,
          [pathQueryParams.challengeId]: challengeId,
        },
      });
    }
  };

  return {
    ...selectSubscriptionInfo,
    handleSubmit,
  };
};
